import { makeRng } from '../helpers'

 type Mountain = {
  x: number
  width: number
  height: number
  color: string
  layer: number
}

type Cloud = {
  x: number
  y: number
  scale: number
  opacity: number
}

type ProceduralScenery = {
  loopWidth: number
  mountains: Mountain[]
  clouds: Cloud[]
}

const createScenery = (seed: number, width: number, height: number): ProceduralScenery => {
  const rng = makeRng(seed)
  const loopWidth = width * 3
  const startX = width * 0.5

  const mountains: Mountain[] = Array.from({ length: 6 }).map((_, idx) => ({
    x: startX + rng() * loopWidth,
    width: width * (0.5 + rng() * 0.8),
    height: height * (0.25 + rng() * 0.2),
    color: idx % 2 === 0 ? '#cdd4e0' : '#bcc5d3',
    layer: idx % 2 === 0 ? 1 : 2,
  }))

  const clouds: Cloud[] = Array.from({ length: 8 }).map(() => ({
    x: startX + rng() * loopWidth,
    y: height * 0.1 + rng() * height * 0.25,
    scale: 0.5 + rng() * 0.8,
    opacity: 0.3 + rng() * 0.35,
  }))

  return {
    loopWidth,
    mountains,
    clouds,
  }
}

const projectX = (input: number, offset: number, loopWidth: number, viewWidth: number) => {
  const value = ((input - offset) % loopWidth + loopWidth) % loopWidth
  return value - viewWidth
}

const drawCloud = (
  ctx: CanvasRenderingContext2D,
  cloud: Cloud,
  width: number,
  height: number,
  offset: number,
  loopWidth: number,
) => {
  const x = projectX(cloud.x, offset * 0.4, loopWidth, width)
  if (x < -80 || x > width + 80) return
  ctx.save()
  ctx.translate(x, cloud.y)
  ctx.scale(cloud.scale, cloud.scale)
  ctx.fillStyle = `rgba(255,255,255,${cloud.opacity})`
  ctx.beginPath()
  ctx.arc(-20, 0, 22, 0, Math.PI * 2)
  ctx.arc(0, -10, 28, 0, Math.PI * 2)
  ctx.arc(20, 0, 22, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

const drawMountain = (
  ctx: CanvasRenderingContext2D,
  mountain: Mountain,
  width: number,
  height: number,
  offset: number,
  loopWidth: number,
) => {
  const x = projectX(mountain.x, offset * (mountain.layer === 1 ? 0.2 : 0.35), loopWidth, width)
  if (x < -mountain.width || x > width + mountain.width) return
  ctx.fillStyle = mountain.color
  ctx.beginPath()
  ctx.moveTo(x, height * 0.85)
  ctx.lineTo(x + mountain.width / 2, height * 0.85 - mountain.height)
  ctx.lineTo(x + mountain.width, height * 0.85)
  ctx.closePath()
  ctx.fill()
}

const drawBackground = (
  ctx: CanvasRenderingContext2D,
  scenery: ProceduralScenery,
  width: number,
  height: number,
  scroll: number,
) => {
  const offset = scroll * 24
  const { mountains, clouds, loopWidth } = scenery

  const skyGradient = ctx.createLinearGradient(0, 0, 0, height)
  skyGradient.addColorStop(0, '#cbd4e2')
  skyGradient.addColorStop(0.5, '#b3bdcf')
  skyGradient.addColorStop(1, '#9daabf')
  ctx.fillStyle = skyGradient
  ctx.fillRect(0, 0, width, height)

  mountains
    .sort((a, b) => a.layer - b.layer)
    .forEach((mountain) => drawMountain(ctx, mountain, width, height, offset, loopWidth))

  clouds.forEach((cloud) => drawCloud(ctx, cloud, width, height, offset, loopWidth))

  ctx.fillStyle = '#d8cfbf'
  ctx.fillRect(0, height * 0.82, width, height * 0.2)

}

export type { ProceduralScenery }
export { createScenery, drawBackground }
